import { waitForNextFrameAsync, waitForRenderableContainerAsync } from "../map-container";
import { IMapInstance, IMapProvider, MapInitOptions } from "../map-provider.interface";
import { HereMapInstance } from "./here-map-instance";

export type HereProviderOptions = {
    apiKey: string;
};

type HereModule = typeof import("@here/maps-api-for-javascript");

export class HereProvider implements IMapProvider {
    private static _herePromise: Promise<HereModule> | null = null;

    private readonly _apiKey: string;
    private _platform: InstanceType<HereModule["service"]["Platform"]> | null = null;

    constructor(options: HereProviderOptions) {
        this._apiKey = options.apiKey;
    }

    async createMap(container: HTMLElement, options: MapInitOptions): Promise<IMapInstance> {
        await waitForRenderableContainerAsync(container);

        const here = await this._getHereAsync();
        const platform = await this._getPlatformAsync();

        const defaultLayers = platform.createDefaultLayers();

        const map = new here.Map(
            container,
            defaultLayers.vector.normal.map,
            {
                center: options.center,
                zoom: options.zoom
            }
        );

        const mapEvents = new here.mapevents.MapEvents(map);
        const behavior = new here.mapevents.Behavior(mapEvents);

        // UI for InfoBubble
        const ui = here.ui.UI.createDefault(map, defaultLayers);
        ui.removeControl('mapsettings');
        ui.removeControl('zoom');

        map.getViewPort().resize();

        await waitForNextFrameAsync();
        map.getViewPort().resize();

        window.addEventListener("resize", () => map.getViewPort().resize());

        return new HereMapInstance(here, map, ui, behavior, platform, container);
    }

    private async _getHereAsync(): Promise<HereModule> {
        HereProvider._herePromise ??= import(
            /* webpackChunkName: "mapsjs" */
            "@here/maps-api-for-javascript"
        ).then(module => module.default);

        return HereProvider._herePromise;
    }

    private async _getPlatformAsync(): Promise<InstanceType<HereModule["service"]["Platform"]>> {
        const here = await this._getHereAsync();

        this._platform ??= new here.service.Platform({
            apikey: this._apiKey
        });

        return this._platform;
    }
}